import { useEffect, useState } from 'react';

import { SafeElectronAPI } from 'src/hooks/useElectron';

export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'downloading'
  | 'ready'
  | 'up_to_date';

interface UpdateState {
  status: UpdateStatus;
  lastCheckedAt: Date | null;
}

let state: UpdateState = { status: 'idle', lastCheckedAt: null };
const listeners = new Set<() => void>();

let cachedApi: SafeElectronAPI | null = null;

function setState(next: Partial<UpdateState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Called once from ElectronIntegration to wire up IPC listeners.
 * Returns a cleanup function to unsubscribe.
 */
export function initElectronUpdateStore(api: SafeElectronAPI) {
  cachedApi = api;

  const unsubAvailable = api.onUpdateAvailable(() => {
    setState({ status: 'downloading' });
  });

  const unsubDownloaded = api.onUpdateDownloaded(() => {
    setState({ status: 'ready' });
  });

  return () => {
    unsubAvailable();
    unsubDownloaded();
    cachedApi = null;
  };
}

function latestVersionFromCheckResult(info: unknown): string | null {
  if (!info || typeof info !== 'object') {
    return null;
  }
  const { version } = info as { version?: unknown };
  return typeof version === 'string' ? version : null;
}

// electron-updater always returns the latest.yml payload, even when the
// installed build already matches. The desktop preload forwards that object
// as-is, so a truthy result does not mean an update is downloading.
function isNewerVersion(latest: string, current: string): boolean {
  const parse = (value: string): number[] =>
    value.split('.').map((part) => Number.parseInt(part, 10) || 0);
  const latestParts = parse(latest);
  const currentParts = parse(current);
  const length = Math.max(latestParts.length, currentParts.length);
  for (let i = 0; i < length; i += 1) {
    const a = latestParts[i] ?? 0;
    const b = currentParts[i] ?? 0;
    if (a !== b) {
      return a > b;
    }
  }
  return false;
}

export async function checkForUpdates() {
  if (!cachedApi) return;

  setState({ status: 'checking' });
  try {
    const [currentVersion, updateInfo] = await Promise.all([
      cachedApi.getVersion(),
      cachedApi.checkForUpdates(),
    ]);
    const latestVersion = latestVersionFromCheckResult(updateInfo);
    const updateIsNewer =
      latestVersion !== null && isNewerVersion(latestVersion, currentVersion);

    setState({
      lastCheckedAt: new Date(),
      status:
        state.status === 'ready'
          ? 'ready'
          : updateIsNewer || state.status === 'downloading'
            ? 'downloading'
            : 'up_to_date',
    });
  } catch (error) {
    console.error('Failed to check for updates', error);
    setState({ lastCheckedAt: new Date(), status: 'up_to_date' });
  }
}

export function useElectronUpdateStatus(): UpdateState {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    // Sync in case state changed between render and effect
    setSnapshot(state);
    return subscribe(() => setSnapshot(state));
  }, []);

  return snapshot;
}
